import {
  generalTerm,
} from '@/constants/locales/de.shared.ts';
import {
  TComponentPT9800Keys, TComponentPT9800Value,
  TComputedPT9800PlannedPartInput,
  Tpt9800TrlTokens,
} from '@/types/openapi-type.ts';

const processFlowManualCommand: TComponentPT9800Keys<'processFlowManualCommand'> = {
  acknowledged: 'Bestätigt',
  additionalInfoColumnCaption: 'Zusätzliche Informationentitel',
  additionalInfoColumnValue: 'Zusätzliche Informationenwert',
  idFieldName: 'ID-Feldname',
  idFieldValue: 'ID-Feldwert',
  ldArchivCrit1Name: 'Archiv-Crit1-Titel',
  ldArchivCrit1Value: 'Archiv-Crit1-Wert',
  orderNumberCaption: 'Auftragnummertitel',
  orderNumberValue: 'Auftragnummerwert',
  priority: 'Priorität',
  rowId: 'Zeilen-Id',
  source: 'Quelle',
  sourceWpId: 'Quell-WP-Id',
  target: 'Ziel',
  targetWpId: 'Ziel-WP-Id',
  wpId: 'Car-WP-Id',
};

const recipeRuntimeInfo: TComponentPT9800Keys<'recipeRuntimeInfo'> = {
  actualStepDuration: 'Aktuelle Schrittzeit',
  loadIdentifier: generalTerm.loadIdentifier,
};

const plannedPartInput: Partial<Record<keyof TComputedPT9800PlannedPartInput, string>> = {
  pos: 'Position',
  partName: 'Teile-Nr.',
  partDescription: 'Teilebeschreibung',
  orderNo: generalTerm.loadIdentifier,
  orderRemark: 'Bestellbemerkung',
  customerOrderNumber: 'Kundenbestell-Nr.',
  amount: 'Menge',
  weight: 'Gewicht',
  totalWeight: 'Gesamtgewicht',
};

const planLoadInput: Partial<TComponentPT9800Keys<'planLoadInput'>> = {
  workplaceWoNumber: generalTerm.loadIdentifier,
};

const recipeListItem: TComponentPT9800Keys<'recipeListItem'> = {
  id: 'Id',
  description: generalTerm.description,
  recipe: generalTerm.recipe,
  recipePrefix: 'Rezeptpräfix',
  version: 'Rezeptversion',
};

const recipeItemHeader: Partial<TComponentPT9800Keys<'batchRecipeItemHeader'>> = {
  recipeName: 'Name',
  version: generalTerm.revision,
  recipePrefix: 'Gruppe',
  description: 'Beschreibung',
  processType: 'Prozess',
  processId: 'Betrieb',
  comment: 'Kommentar',
  diff: 'Aktivieren',
  correction: 'Korrektur',
};

const material: Partial<TComponentPT9800Keys<'material'>> = {
  // Diffusion field labels reuse the shared generalTerm wording so they read identically here, in
  // the Diffusion Inputs panel and in the simulator.
  carbonContent: generalTerm.carbonContent,
  carbideLimit: generalTerm.carbideLimit,
  alloyFactor: generalTerm.alloyFactor,
  customerMaterialId: 'Werkstoff',
  endTemperature: generalTerm.endTemperature,
  grainSize: generalTerm.grainSize,
  quenchingIntensity: generalTerm.quenchingIntensity,
  representativeDiameter: generalTerm.representativeDiameter,
  requiredHardness: generalTerm.requiredHardness,
  targetSurfC: generalTerm.targetSurfaceCarbon,
  co: generalTerm.carbonMonoxide,
  h2: generalTerm.hydrogen,
};

const beltRecipeItemHeader: Partial<TComponentPT9800Keys<'beltRecipeItemHeader'>> = {
  description1: 'Bezeichnung 1',
  description2: 'Bezeichnung 2',
  description3: 'Bezeichnung 3',
  description4: 'Bezeichnung 4',
  processId: 'Verfahren',
  processDepth: 'Aufkohlungstiefe',
  processDepthAt: 'Kohlenstoffgehalt bei Aufkohlungstiefe',
};

const pusherRecipeItemHeader: Partial<TComponentPT9800Keys<'pusherRecipeItemHeader'>> = {
  description: beltRecipeItemHeader.description1,
  description1: beltRecipeItemHeader.description2,
  processDepth: beltRecipeItemHeader.processDepth,
  processDepthAt: beltRecipeItemHeader.processDepthAt,
  caseHardeningDepthFrom: 'Einsatzhärtungstiefe von',
  caseHardeningDepthTo: 'Einsatzhärtungstiefe bis',
  endTemperature: generalTerm.endTemperature,
  grainSize: generalTerm.grainSize,
  quenchIntensity: generalTerm.quenchingIntensity,
  partId: generalTerm.part,
};

const pusherRecipeRuntimeInfo: Partial<TComponentPT9800Keys<'pusherRecipeRuntimeInfo'>> = {
  timeStamp: generalTerm.date,
  description: 'Platzbezeichnung',
  instanceNoteText1: 'Bemerkung 1',
  instanceNoteText2: 'Bemerkung 2',
  instanceNoteText3: 'Bemerkung 3',
};

const recipeItemPosition: Partial<TComponentPT9800Keys<'recipeItemPosition'>> = {
  setpoints: 'Sollwerte',
  endAfter: 'Endbedingung',
  digitalOutputList: 'Digitale Ausgänge',
  digitalInputList: 'Digitale Eingänge',
};

const processFlowContent: Partial<TComponentPT9800Keys<'processFlowContent'>> = {
  name: 'Inhalt-Nr.',
  weight: 'SPS Gewicht',
  createdTimestamp: 'Erstellt am',
  modifiedTimestamp: 'Geändert am',
};

const processFlow: Partial<TComponentPT9800Keys<'processFlow'>> = {
  name: 'Name',
  description: 'Beschreibung',
  updateTimestamp: 'Letzte Aktualisierung',
  automatNo: 'Automat-Nr.',
  comment1: 'Kommentar 1',
  comment2: 'Kommentar 2',
  comment3: 'Kommentar 3',
};

const processFlowListItem: Partial<TComponentPT9800Keys<'processFlowListItem'>> = {
  name: generalTerm.name,
  description: generalTerm.description,
};

const recipeItemPositionHeader: Partial<TComponentPT9800Keys<'recipeItemPositionHeader'>> = {
  positionNo: 'Schritt',
  description: 'Schrittname',
  chamberId: 'Kammer',
  chamberOperationId: 'Kammerbetrieb',
  afterId: 'Endbedingung',
  estimatedDurationInMin: 'Geschätzte Dauer',
  endAtPerc: 'Ende bei %cd',
  afterTimeInMin: 'Dauer der Endbedingung',
};

const setPointItem: Partial<TComponentPT9800Keys<'setPointItem'>> & {
  unit: string,
} = {
  userComponentId: 'Sollwert',
  controlId: 'Ctrl',
  value: 'Wert',
  unit: 'Einheit',
  toleranceMax: 'Tol +',
  toleranceMin: 'Tol -',
  alarmStartTimeInMin: 'Störungsbeginn',
  alarmToleranceTimeInMin: 'Störungstoleranz',
  endControlId: 'Endbedingung',
  endValue: 'Endbedingung',
};

const furnaceAlarm: Partial<TComponentPT9800Keys<'furnaceAlarm'>> = {
  wpName: 'Arbeitsplatz',
  ackTime: 'Bestätigungszeit',
  alarmBit: 'Bit',
  alarmByte: 'Byte',
  endTime: 'Endzeit',
  gate: 'Tor',
  alarmNo: 'Id',
  message: 'Nachricht',
  priority: 'Priorität',
  startTime: 'Startzeit',
};

const load: Partial<TComponentPT9800Keys<'load'>> = {
  name: generalTerm.jobDisplayName,
  startTime: generalTerm.jobStartedAtUtc,
  endTime: generalTerm.jobFinishedAtUtc,
  identifier: generalTerm.loadIdentifier,
  recipe: generalTerm.recipe,
  partsText: plannedPartInput.partDescription,
  workOrderNo: plannedPartInput.orderNo,
  orderRemark: plannedPartInput.orderRemark,
  status: 'Status',
  comment: 'Kommentar',
  processFlowLoadName: 'Prozessablauf-Charge',
  packedTime: 'Packzeit',
  packedUser: 'Gepackt von',
  startedUser: 'Gestartet von',
  earliestTimeStart: 'Frühester Start',
};

const loadState: Record<TComponentPT9800Value<'load'>['status'], string> = {
  EXT_START: 'Gestartet (extern)',
  FINISHED: 'Beended',
  PLANNED: 'Geplant',
  RUNNING: 'Läuft',
  RUNNING_LOADING_ENDED: 'Läuft (Belanden abgeschlossen)',
  STARTED: 'Gestartet',
  STOPPED: 'Gestopped',
  TOO_OLD_NEVER_STARTED: 'Zu alt / Nie gestartet',
  UNKNOWN: 'Unbekannt',
  WAITING_FOR_CONDITIONS: 'Warte auf Startbedingung',
  WAITING_FOR_LOADING: 'Warte auf Beladung',
};

const WorkPlaceStatus: Partial<TComponentPT9800Keys<'WorkPlaceStatus'>> = {
  connected: 'Verbindungsstatus',
  recipe: 'Rezeptschritt',
  remote: 'Fernsteuerung',
  hold: 'Angehalten',
};

const WorkPlaceStatusStatus: Record<Exclude<TComponentPT9800Value<'WorkPlaceStatus'>['status'], undefined>, string> = {
  ALARM: 'Störung',
  IDLE: 'Leerlauf',
  NO_REMOTE_CONNECTION: 'Keine Remote-Verbindung',
  RUNNING: 'Laufendes Rezept',
  SWITCHED_OFF: 'Ausgeschaltet',
};

export const pt9800Translations = {
  T_MENU_GROUP_USER: 'Benutzer',
  T_MENU_GROUP_TERMINAL: 'Terminal',
  T_MENU_GROUP_SYSTEM_1: 'System-1',
  T_MENU_GROUP_SYSTEM_2: 'System-2',
  T_MENU_ITEM_USER_GROUPS: 'Benutzergruppen',
  T_MENU_ITEM_USERS: 'Benutzer',
  T_MENU_ITEM_APPLICATION_GROUPS: 'Anwendungsgruppen',
  T_MENU_ITEM_APPLICATIONS: 'Anwendungen',
  T_MENU_ITEM_USER_ACCESS: 'Benutzerzugriff',
  T_MENU_ITEM_MATERIAL_MANAGEMENT: 'Werkstoffverwaltung',
  T_MENU_ITEM_PART_MANAGEMENT: 'Teileverwaltung',
  T_MENU_ITEM_PART_CUSTOMERS: 'Kunden',
  T_MENU_ITEM_TERMINAL_TYPE: 'Terminaltyp',
  T_MENU_ITEM_TERMINAL: 'Terminal',
  T_MENU_ITEM_CLIENT_ACCESS: 'Clientzugriff',
  T_MENU_ITEM_COLOR_ADMINISTRATION: 'Farben',
  T_MENU_ITEM_BOX_ADMINISTRATION: 'Boxen',
  T_MENU_ITEM_ALARMS: 'Störungen',
  T_MENU_ITEM_BACKGROUND_PROCESSES: 'Hintergrundprozesse',
  T_MENU_ITEM_TASKS: 'Chargen',
  T_MENU_ITEM_WORKPLACES: 'Arbeitsplätze',
  T_MENU_ITEM_OPERATIONS: 'Vorgänge',
  T_MENU_GROUP_SYSTEM_DATA: 'Systemdaten',
  T_MENU_GROUP_EVALUATION: 'Auswertung',
  T_MENU_GROUP_PART: 'Teil',
  T_MENU_GROUP_SPECIAL: 'Spezial',
  T_MENU_ITEM_PRODUCTION_LOGS: 'Produktionsprotokolle',
  T_MENU_ITEM_WORKPLACE_TYPE: 'Arbeitsplatztyp',

  T_SHOP_FURNACE_STATUS_NOT_CONNECTED: 'Blau=Anlage nicht verbunden',
  T_SHOP_FURNACE_STATUS_MANUAL_MODE: 'Weiß=Handbetrieb',
  T_SHOP_FURNACE_STATUS_NO_TREATMENT_RUNNING: 'Gelb=Keine Wärmebehandlung aktiv',
  T_SHOP_FURNACE_STATUS_PLC_DENIES_START: 'Pink=SPS verweigert Rezeptstart',
  T_SHOP_FURNACE_STATUS_RUNNING: 'Grün=Wärmebehandlung läuft – keine Störungen',
  T_SHOP_FURNACE_STATUS_ALARM: 'Rot=Störungen vorhanden',
  T_SHOP_FURNACE_STATUS_OFF: 'Hellgrau=Ausgeschaltet (siehe Basisdaten Arbeitsplatz)',
  T_SHOP_FURNACE_STATUS_MAINTENANCE: 'Dunkelgrau=In Wartung (siehe Basisdaten Arbeitsplatz)',
  T_SHOP_FURNACE_STATUS_CONFIG_ERROR: 'Cyan=Fehlerhafte Konfiguration',
  T_SHOP_FURNACE_STATUS_ENERGY_MAN_DISABLED_START: 'Pink=Energiemanagement blockiert Rezeptstart',
  T_SHOP_FURNACE_STATUS_WEEKEND_MODE: 'Wochenendbetrieb',
  T_SHOP_FURNACE_STATUS_NO_ALARMS: 'Grün=Keine Störungen',

  // SHOPLAYOUT - CAR
  T_SHOP_STATUS_CELL_READY_FOR_WORK: 'Anlage ist betriebsbereit',
  T_SHOP_STATUS_CELL_NOT_READY_FOR_WORK: 'Anlage ist NICHT betriebsbereit !!! ',

  T_SHOP_CAR_STATUS_NO_PLC_SIMULATE: '!!! OHNE SPS ⇒ Simulation !!!',
  T_SHOP_CAR_STATUS_MANUAL: 'HAND',
  T_SHOP_CAR_STATUS_AUTO: 'Auto',
  T_SHOP_CAR_STATUS_SCHED: 'SCHED',
  T_SHOP_CAR_STATUS_NOT_ACTIVE: 'BESCHICKUNGSWAGEN NICHT AKTIV',
  T_SHOP_CAR_STATUS_LOGIC_NOT_RUNNING: 'Logik nicht aktiv',
  T_SHOP_CAR_STATUS_NOT_IN_ZERO_POS: 'Nicht in Nullposition',
  T_SHOP_CAR_STATUS_S_ACTIVE: 'SK aktiv',
  T_SHOP_CAR_STATUS_CRANE: 'Kran',
  T_SHOP_CAR_STATUS_S: 'S',
  T_SHOP_CAR_STATUS_T: 'T',
  T_SHOP_CAR_STATUS_CREQ: 'CReq',
  T_SHOP_CAR_STATUS_REQ: 'Req',
  T_SHOP_CAR_STATUS_WANT_CANCEL: 'Abbruch angefordert',
  T_SHOP_CAR_STATUS_CANCEL: 'Abbruch',
  T_SHOP_CAR_STATUS_NOT_VALID: 'Ungültig',
  T_SHOP_CAR_STATUS_REF: 'Ref.',
  T_SHOP_CAR_STATUS_RUNNING: 'In Betrieb',
  T_SHOP_CAR_STATUS_FINISHED: 'Beendet',
  T_SHOP_CAR_STATUS_WILL_END: 'Wird beendet',
  T_SHOP_CAR_STATUS_TOS: 'zu S',
  T_SHOP_CAR_STATUS_LDS: 'lade S',
  T_SHOP_CAR_STATUS_SLD: 'S geladen',
  T_SHOP_CAR_STATUS_TOD: 'zu D',
  T_SHOP_CAR_STATUS_DL: 'entlade',
  T_SHOP_CAR_STATUS_DLD: 'D entladen',

  T_SHOP_CAR_CONTROL_CRANE: 'Kran im SCHED-Modus',
  T_SHOP_CAR_1_SCHED_MODE: 'Wagen 1 SCHED-Modus',
  T_SHOP_CAR_2_SCHED_MODE: 'Wagen 2 SCHED-Modus',
  T_SHOP_CAR_3_SCHED_MODE: 'Wagen 3 SCHED-Modus',
  T_SHOP_CAR_4_SCHED_MODE: 'Wagen 4 SCHED-Modus',
  T_SHOP_CAR_OIL_QUENCH_SCHEDULED: 'Ölabschrecken geplant',
  T_SHOP_CAR_IN_SCHEDULED_MODE: 'Wagen im SCHED-Modus',
  T_SHOP_CAR_SCHEDULED: 'SCHED-Betrieb',
  T_SHOP_STORAGE_SCHEDULED: 'Lager im SCHED-Modus',

  // Stellplatz – Text unter Box
  T_SHOP_PLACE_STATUS_FINISHED: 'Beendet',
  T_SHOP_PLACE_STATUS_CHECK: 'Prüfen',
  T_SHOP_PLACE_STATUS_ASAP: 'P.Eilig',
  T_SHOP_PLACE_STATUS_PCHECK: 'P.Kontr',
  T_SHOP_PLACE_STATUS_WCHECK: 'W.Kontr..',
  T_SHOP_PLACE_STATUS_WAIT: 'Warten',
  T_SHOP_PLACE_STATUS_MANUAL: 'Manuell',

  // SHOPLAYOUT TOOLTIPS
  T_SHOP_TT_LOCK_COMMENT: 'Sperrkommentar',
  T_SHOP_TT_LOAD_NO: 'Chargen-Nr.',
  T_SHOP_TT_IS_ON_PLACE: 'befindet sich auf Platz',
  T_SHOP_TT_PLACE: 'Platz',
  T_SHOP_TT_BACK_TO_PLACE: 'Zurück zu Arbeitsplatz-Nr.',
  T_SHOP_TT_PRIORITY: 'Priorität',
  T_SHOP_TT_IS_RESERVED_FROM: 'reserviert ab',
  T_SHOP_TT_TEST_LOAD: 'Testcharge',
  T_SHOP_TT_LONG_LOAD: 'Langcharge',
  T_SHOP_TT_PF_NO: 'Ablauf-Nr.',
  T_SHOP_TT_RECIPE: 'Rezept',
  T_SHOP_TT_PLANNED: 'Geplant',
  T_SHOP_TT_STARTED: 'Gestartet',
  T_SHOP_TT_PREPARE_FROM: 'Vorbereiten ab',
  T_SHOP_TT_STARTING_TIME: 'Startzeit',
  T_SHOP_TT_ENDING_TIME: 'Endzeit',
  T_SHOP_TT_STEP_NO: 'Schritt-Nr.',
  T_SHOP_TT_PLANNING_SORTING: 'Planungssortierung (1–99)',
  T_SHOP_TT_PF_COMPLETED: 'Ablauf abgeschlossen',
  T_SHOP_TT_EMPTY_RACK: 'Leeres Gestell',
  T_SHOP_TT_NO_INFO: '---',
  T_SHOP_TT_ORDER: 'Auftrag',
  T_SHOP_TT_SERIAL_NO: 'Serien-Nr.',
  T_SHOP_TT_PART_DES: 'Teilebezeichnung',
  T_SHOP_TT_PART_NO: 'Teile-Nr.',
  T_SHOP_TT_CUSTOMER: 'Kunde',
  T_SHOP_TT_IN_STAGE: 'im Schritt:',

  // SHOPLAYOUT, PROCESS FLOWS
  T_SHOP_PF_STATUS_FREE: 'Frei',
  T_SHOP_PF_STATUS_OCCUPIED: 'Belegt',
  T_SHOP_PF_STATUS_FINISHED: 'Fertig',
  T_SHOP_PF_STATUS_ERROR: 'Fehler',
  T_SHOP_PF_STATUS_LOCKED: 'GESPERRT',
  T_SHOP_PF_STATUS_RESERVED: 'Reserviert',
  T_SHOP_PF_STATUS_LOCKED_HIGH_LOAD: 'Gesperrt wegen hoher Charge',
  T_SHOP_PF_STATUS_OILY: 'Ölig',
  T_SHOP_PF_STATUS_WAIT_FOR_PLC: 'Muss-an-SPS',
  T_SHOP_PF_STATUS_EMPTY_RACK: 'Leerrost',
  T_SHOP_PF_STATUS_EMPTY_RACK_ANNEALING_GOODS: 'Leerrost Glühware',
  T_SHOP_PF_STATUS_BASE_TRAY_ANNEALING_GOODS: 'Grundrost Glühware',
  T_SHOP_PF_STATUS_EMPTY_RACK_STAPLE_GOODS: 'Leerrost Stapelware',
  T_SHOP_PF_STATUS_BASE_TRAY_PRODUCTS: 'Grundrost Stapelware',
  T_SHOP_PF_STATUS_TEST_LOAD: 'Prüfcharge',
  T_SHOP_PF_STATUS_BOTTOM_OILY: 'Unten-ölig',
  T_SHOP_PF_STATUS_CHECK: 'Zprüf',
  T_SHOP_PF_STATUS_PROCESS_CHECK: 'P.Kontr.',
  T_SHOP_PF_STATUS_WASCH_CHECK: 'W.Kontr.',
  T_SHOP_PF_STATUS_WAIT: 'Warten',
  T_SHOP_PF_STATUS_MANUAL: 'Manuell',

  T_SHOP_PF_STATUS_RESERVED_TEXT: 'Reserviert von ',
  T_SHOP_PF_STATUS_RESERVED_TEXT_LOAD: 'Abl. Charge Nr:',

  T_SHOP_PF_PRIORITY_NORMAL: 'Normal',
  T_SHOP_PF_PRIORITY_HURRIED: 'Eilig',
  T_SHOP_PF_PRIORITY_VERY_FAST: 'Sehr eilig',
  T_SHOP_PF_PRIORITY_AS_FAST_AS_POSSIBLE: 'Schnellst-Möglichst',

  // Automat shoplayout legend
  T_SHOP_PF_LEGEND_TEST_PASS: 'Freigegeben',
  T_SHOP_PF_LEGEND_CLEANED: 'Gereinigt',
  T_SHOP_PF_LEGEND_PRE_OXIDIZED: 'Voroxidiert',
  T_SHOP_PF_LEGEND_PRE_HEATED: 'vorgewärmt',
  T_SHOP_PF_LEGEND_TEMPERED: 'Angelassen',
  T_SHOP_PF_LEGEND_UNLOAD: 'Entladen',
  T_SHOP_PF_LEGEND_EMPTY_RACK: 'Leerrost',
  T_SHOP_PF_LEGEND_LOCKED: 'Gesperrt',
  T_SHOP_PF_LEGEND_DELAYED_START: 'Startzeit',
  T_SHOP_PF_LEGEND_PREHEATING: '->Vorwärmen',
  T_SHOP_PF_LEGEND_TO_IQ: '->MZK',
  T_SHOP_PF_LEGEND_TO_DROPPING: '->Abtropfen',
  T_SHOP_PF_LEGEND_TO_COOLING: '->Kühlen',
  T_SHOP_PF_LEGEND_TO_WASCHING: '->Waschen',
  T_SHOP_PF_LEGEND_TO_TEMPERING: '->Anlassen',
  T_SHOP_PF_LEGEND_LOAD_IN_PROGRESS: 'Charge in Prozess',
  T_SHOP_PF_LEGEND_IN_PROCESS_FLOW: 'Im Ablauf',
  T_SHOP_PF_LEGEND_TEST_LOAD: 'Prüfcharge',
  T_SHOP_PF_LEGEND_FOR_WASHING: 'zum Waschen',
  T_SHOP_PF_LEGEND_FOR_NITRIDING: 'zum Nitrieren',
  T_SHOP_PF_LEGEND_TEST_CHARGE_IN_PROGRESS: 'Prüfcharge in Arbeit',
  T_SHOP_PF_LEGEND_INCOMPLETE: 'Unvollständig',
  T_SHOP_PF_LEGEND_TO_PACKAGING: 'zumAbpacken',
  T_SHOP_PF_LEGEND_FINISHED_LOAD: 'Charge beendet',
  T_SHOP_PF_LEGEND_FINISHED_WITH_ALARMS: 'Charge beendet mit Störungen',
  T_SHOP_PF_LEGEND_WAIT: 'Warten',
  T_SHOP_PF_LEGEND_TEST_WAIT: 'Warten',
  T_SHOP_PF_LEGEND_TEST_LOAD_URGENT: 'Prüfcharge Eilig',
  T_SHOP_PF_LEGEND_TEST_PROCESS_INSPECTION: 'Prozesskontrolle',
  T_SHOP_PF_LEGEND_TEST_WASH_INSPECTION: 'Waschkontrolle',
  T_SHOP_PF_LEGEND_IN_BETWEEN: 'Zwischengelagert',
  T_SHOP_PF_LEGEND_RESERVED: 'Reserviert',
  T_SHOP_PF_LEGEND_GR_G: 'GR-G.',
  T_SHOP_PF_LEGEND_LR_G: 'LR-G.',
  T_SHOP_PF_LEGEND_GR_S: 'GR-S.',
  T_SHOP_PF_LEGEND_LR_S: 'LR-S.',

  T_SHOP_PF_MESSAGE_SECOND_LOAD_ASSIGNED: 'Dieser Charge ist eine zweite Charge zugeordnet, bitte zuerst entfernen',
  T_SHOP_PF_MESSAGE_CONFIRMATION_NEXT_STAGE: 'Wollen Sie WIRKLICH den aktuellen Schritt von Hand als beendet deklarieren und den nächsten Schritt aktivieren ?',
  T_SHOP_PF_MESSAGE_WARNING_DANGEROUS: '!!! ACHTUNG !!! Dies ist eine sehr gefährliche Aktion',
  T_SHOP_PF_MESSAGE_NOT_VALID: 'Ungültig',
  T_SHOP_PF_MESSAGE_STEP_MANIPULATION: 'Schrittmanipulation',
  T_SHOP_PF_MESSAGE_ERROR_PLACE: 'Fehler: Platz:',
  T_SHOP_PF_MESSAGE_ERROR_PF: 'Fehler: Ablauf:',
  T_SHOP_PF_MESSAGE_IS_NOT_OCCUPIED: 'ist nicht belegt',
  T_SHOP_PF_MESSAGE_HAS_NO_LOAD: 'hat keine Charge',
  T_SHOP_PF_MESSAGE_HAS_NO_STAGE: 'hat keinen Schritt',
  T_SHOP_PF_MESSAGE_NO_FURTHER_LOADS_PERMITTED: 'Keine weiteren Chargen erlaubt!',
  T_SHOP_PF_MESSAGE_SELECT_START_TIME: 'Startzeit auswählen',
  T_SHOP_PF_MESSAGE_LOAD_READY_NEW_STEP: 'Charge ist bereit. Wegen neuem Schritt reaktivieren?',
  T_SHOP_PF_MESSAGE_SELECT_PERSON_FOR_PACK: 'Person für <Charge verpacken> auswählen',
  T_SHOP_PF_MESSAGE_MAY_NOT_BE_EMPTY: 'darf nicht leer sein',
  T_SHOP_PF_MESSAGE_ENTER_BOX_NO: 'Bitte Box-Nr. eingeben!',
  T_SHOP_PF_MESSAGE_ENTER_VALID_BOX_NO: 'Bitte gültige Box-Nr. auswählen (10..49)',
  T_SHOP_PF_MESSAGE_CANNOT_PICK_TALL_LOADS: 'Hohe Chargen können nicht aufgenommen werden',
  T_SHOP_PF_MESSAGE_BOOK_PROCESS_FLOW: 'Ablauf buchen',
  T_SHOP_PF_MESSAGE_SELECT_TRAY_CODE: 'Bitte Gestellcode auswählen (1..99)!',
  T_SHOP_PF_MESSAGE_ENTER_LOAD_ORDER_1: 'Bitte Lade-Reihenfolge wählen (1..700)',
  T_SHOP_PF_MESSAGE_ENTER_LOAD_ORDER_2: 'Bitte Lade-Reihenfolge kleiner als 701 wählen!',
  T_SHOP_PF_MESSAGE_ENTER_VALID_TOOLING: 'Bitte gültige Vorrichtung eingeben',
  T_SHOP_PF_MESSAGE_WAIT_PLC_CHECK_PROGRESS: 'Bitte warten. Die Prüfung mit der PLC läuft ...',
  T_SHOP_PF_MESSAGE_MOVE_DATA_TARGET_OCCUPIED: 'Das Ziel ist bereits belegt. Möchten Sie es wirklich überschreiben?',
  T_SHOP_PF_MESSAGE_MOVE_DATA_TARGET_OCCUPIED_TRAY: 'PlatzControl.80=Das Ziel ist bereits mit einem leeren Tray belegt. Möchten Sie es wirklich überschreiben?',
  T_SHOP_PF_MESSAGE_MOVE_OCCUPIED_DELETE: 'ist belegt → vielleicht können Sie es löschen.',
  T_SHOP_PF_MESSAGE_MOVE_OCCUPIED_LOAD_DELETE: 'ist durch eine Charge belegt → vielleicht können Sie diese löschen.',
  T_SHOP_PF_MESSAGE_OCCUPY_ALREADY_OCCIPIED: 'Der Platz ist bereits belegt!',
  T_SHOP_PF_MESSAGE_OCCUPY_CONFIRM: 'Möchten Sie den Platz als belegt markieren?',
  T_SHOP_PF_MESSAGE_NO_PF_CONTENT_FOUND: 'Kein ablauf inhalt gefunden',
  T_SHOP_PF_MESSAGE_CONFIRMATION_DATA_ON_PLACE_DELETION: 'Platz hat noch Daten, aber kein BELEGT ! Sperre aufheben würde Löschung bedeuten. <Ja> == Trotzdem durchführen ?',
  T_SHOP_PF_MESSAGE_CONFIRMATION_DATA_ON_PLACE_DELETION_LAST_WARNING: '!!!!!!!!!!!!! LETZTE WARNUNNG !!!!!!!!!!!! Sperre aufheben würde LÖSCHUNG bedeuten. <Ja> == Platz ABLÖSCHEN ?',
  T_SHOP_PF_MESSAGE_NO_RIGHTS_FOR_ACTION: 'Sie haben keine Rechte um diese Aktion auszufuehren. Bitte an ihren Vorgesetzten wenden.',

  // Process flow table columns
  T_SHOP_PF_COLUMN_OPERATION_LABEL: 'Verfahren',
  T_SHOP_PF_COLUMN_WORKPLACE_LABEL: 'Anlage',
  T_SHOP_PF_COLUMN_RECIPE_LABEL: 'Behandlung',
  T_SHOP_PF_COLUMN_DESCRIPTION_LABEL: 'Bezeichnung',
  T_SHOP_PF_COLUMN_TIME_LABEL: 'Zeit',
  T_SHOP_PF_COLUMN_TIME_TOOLTIP: 'Warte-Zeit[min] auf diesem Platz',
  T_SHOP_PF_COLUMN_ACK_LABEL: 'R',
  T_SHOP_PF_COLUMN_ACK_TOOLTIP: 'Rückmelden',
  T_SHOP_PF_COLUMN_AUTOSKIP_LABEL: 'A',
  T_SHOP_PF_COLUMN_AUTOSKIP_TOOLTIP: 'AutoSkip',
  T_SHOP_PF_COLUMN_MANUAL_LABEL: 'H',
  T_SHOP_PF_COLUMN_MANUAL_TOOLTIP: 'Hand-An-Ab-Melden',
  T_SHOP_PF_COLUMN_QM_RELEVAT_LABEL: 'Q',
  T_SHOP_PF_COLUMN_QM_RELEVAT_TOOLTIP: 'QS-Relevanter-Schritt',
  T_SHOP_PF_COLUMN_QM_RELEVAT_WITTEN_LABEL: 'F',
  T_SHOP_PF_COLUMN_QM_RELEVAT_WITTEN_TOOLTIP: 'Fräsen (externe Bearbeitung)',
  T_SHOP_PF_COLUMN_PROD_STAT_RELEVANT_LABEL: 'P',
  T_SHOP_PF_COLUMN_PROD_STAT_RELEVANT_TOOLTIP: 'Produktions-Statistik-Relevanter-Schritt',
  T_SHOP_PF_COLUMN_MOVE_STEP_LABEL: 'Z',
  T_SHOP_PF_COLUMN_MOVE_STEP_TOOLTIP: 'Planungstechnisch Schritt ranziehen',
  T_SHOP_PF_COLUMN_PTIME_LABEL: 'PZeit',
  T_SHOP_PF_COLUMN_PTIME_TOOLTIP: 'PlanungsZeit',
  T_SHOP_PF_COLUMN_MIN_HOLD_LABEL: 'Min. Zeit',
  T_SHOP_PF_COLUMN_MIN_HOLD_TOOLTIP: 'Mindest-Halte-Zeit[min] im Rezept',
  T_SHOP_PF_COLUMN_MAX_HOLD_LABEL: 'Max. Zeit',
  T_SHOP_PF_COLUMN_MAX_HOLD_TOOLTIP: 'Maximum-Halte-Zeit[min] im Rezept',
  T_SHOP_PF_COLUMN_WISH_TIME_LABEL: 'Planzeit',
  T_SHOP_PF_COLUMN_WISH_TIME_TOOLTIP: 'Planungs-Wunsch-Zeit[min]',
  T_SHOP_PF_COLUMN_STEPNO_FROM_LABEL: 'Kombiniert ab Schritt',
  T_SHOP_PF_COLUMN_STEPNO_FROM_TOOLTIP: 'Schritt-Nr (ab) dem die Min/Max-Zeiten gelten',
  T_SHOP_PF_COLUMN_STEPNO_TO_LABEL: 'Kombiniert bis Schritt',
  T_SHOP_PF_COLUMN_STEPNO_TO_TOOLTIP: 'Schritt-Nr (bis zu) dem die Min/Max-Zeiten gelten',
  T_SHOP_PF_COLUMN_TRANSFER_LABEL: 'Transfer SP-Zeit (Sek)',
  T_SHOP_PF_COLUMN_TRANSFER_TOOLTIP: 'Soll-Transferzeit in Sekunden von vorigem Schritt zu diesem Schritt',
  T_SHOP_PF_COLUMN_PLAN_TRANSFER_LABEL: 'Transfer Planzeit (Min)',
  T_SHOP_PF_COLUMN_PLAN_TRANSFER_TOOLTIP: 'Planungs-Transferzeit in Minuten von vorigem Schritt zu diesem Schritt',
  T_SHOP_PF_COLUMN_DROP_TIME_LABEL: 'SP-Abwurfzeit (Sek)',
  T_SHOP_PF_COLUMN_DROP_TIME_TOOLTIP: 'Soll-Abtropfzeit in Sekunden',
  // runtime
  T_SHOP_PF_COLUMN_START_LABEL: 'Start',
  T_SHOP_PF_COLUMN_END_LABEL: 'Ende',
  T_SHOP_PF_COLUMN_PLANNED_WP_LABEL: 'gepl. Arb.Platz',
  T_SHOP_PF_COLUMN_STATUS_LABEL: 'Status',
  T_SHOP_PF_COLUMN_CHECKED_LABEL: 'Geprüft',
  T_SHOP_PF_COLUMN_TRANSFER_TIME_LABEL: 'Transferzeit[Sek]',
  T_SHOP_PF_COLUMN_SOAK_TIME_LABEL: 'Einweichzeit Messwert',
  T_SHOP_PF_COLUMN_SOAK_TIME_TOOLTIP: 'Wie lange war die Haltezeit der Charge in diesem Schritt',
  T_SHOP_PF_COLUMN_TEMP_AFTER_LABEL: 'Temp. nach Planung',
  T_SHOP_PF_COLUMN_TEMP_AFTER_TOOLTIP: 'Welche Temperatur hat die Anlage NACH der Planung des Schrittes',
  T_SHOP_PF_COLUMN_PREPARE_START_LABEL: 'Vorbereitung Startzeit',
  T_SHOP_PF_COLUMN_PREPARE_START_TOOLTIP: '(Geplante) Startzeit der Umstellung für diesen Schritt',
  T_SHOP_PF_COLUMN_DYN_SETPOINT_LABEL: 'Dynamischer Sollwert',
  T_SHOP_PF_COLUMN_DYN_SETPOINT_TOOLTIP: 'Dynamischer Sollwert durch den Ablaufschritt (Planzeit..Engplanen...)',

  // Process flow table columns - parts
  T_SHOP_PF_COLUMN_PART_POS_LABEL: 'Pos',
  T_SHOP_PF_COLUMN_PART_POS_TOOLTIP: 'Positions-Nr. (laufende Nr.)',
  T_SHOP_PF_COLUMN_PART_PARTNO_TOOLTIP: 'Teile-Nr. eingeben (oder rechts daneben auf Liste klicken)',
  T_SHOP_PF_COLUMN_PART_DESC_LABEL: 'Teil-Bez:',
  T_SHOP_PF_COLUMN_PART_DESC_TOOLTIP: 'Teilebezeichnung',
  T_SHOP_PF_COLUMN_PART_AMOUNT_LABEL: 'Menge',
  T_SHOP_PF_COLUMN_PART_AMOUNT_2_LABEL: 'Menge-Nachkontrolle',
  T_SHOP_PF_COLUMN_PART_AMOUNT_TOTAL_WEIGHT_LABEL: 'Ges. Gewicht',
  T_SHOP_PF_COLUMN_PART_ORDER_LABEL: 'Auftrag',
  T_SHOP_PF_COLUMN_PART_ORDER_TOOLTIP: 'Auftrag-Nr. eingeben (oder rechts daneben auf Liste klicken)',
  T_SHOP_PF_COLUMN_PART_ORDER_SELECTION_LABEL: 'Freigabe',
  T_SHOP_PF_COLUMN_PART_ORDER_REMARK_LABEL: 'Serien-Nr.',
  T_SHOP_PF_COLUMN_PART_ORDER_REMARK_LABEL_2: 'Auftrag-Bemerkung',
  T_SHOP_PF_COLUMN_PART_CUSTOM_FIELD_3: 'Feld-3',
  T_SHOP_PF_COLUMN_PART_CUSTOM_FIELD_4: 'Feld-4',
  T_SHOP_PF_COLUMN_PART_EDIT_IN_WINDOW_LABEL: 'weitere',
  T_SHOP_PF_COLUMN_PART_EDIT_IN_WINDOW_TOOLTIP: 'Bearbeiten in einem Fenster',
  T_SHOP_PF_COLUMN_PART_TYPE_LABEL: 'Art',
  T_SHOP_PF_COLUMN_PART_TYPE_TOOLTIP: 'Art (Eigen,Fremd,Lohn)',
  T_SHOP_PF_COLUMN_PART_START_BASEDATA_PARTS_LABEL: '-> Teile-Stamm',
  T_SHOP_PF_COLUMN_PART_START_BASEDATA_PARTS_TOOLTIP: 'Teilestamm aufrufen',
  T_SHOP_PF_COLUMN_PART_INTERNAL_ORDER_NO_LABEL: 'Intern', // Intern
  T_SHOP_PF_COLUMN_PART_INTERNAL_ORDER_NO_TOOLTIP: 'Auftrags-Id (interner Gebrauch)',
  T_SHOP_PF_COLUMN_PART_PROBE_LABEL: 'Probe',
  T_SHOP_PF_COLUMN_PART_MATERIAL_LABEL: 'Werkstoff',
  T_SHOP_PF_COLUMN_PART_DRAWING_NO_LABEL: 'Zeichnungs-Nr',

  // edit pf
  T_SHOP_PF_DIALOG_EDIT_CAPTION: 'Ablauf eingrigg',

  T_SHOP_PF_MESSAGE_PLACE_ALREADY_RESERVED: 'Neuer Buchungsplatz ist bereits reserviert – Änderung nicht möglich',
  T_SHOP_PF_MESSAGE_NUMBER_USED_1: 'Nummer',
  T_SHOP_PF_MESSAGE_NUMBER_USED_2: 'bereits verwendet!',
  T_SHOP_PF_MESSAGE_NUMBER_USED_3: 'Nummer abgelehnt!',

  T_SHOP_PF_MESSAGE_NOT_COMPLETE_1: 'im Schritt',
  T_SHOP_PF_MESSAGE_NOT_COMPLETE_2: 'nicht vollständig',

  T_SHOP_PF_MESSAGE_AMMOUNT_TOO_SMALL_1: 'Menge zu klein → verwende Ablauf:',
  T_SHOP_PF_MESSAGE_AMMOUNT_TOO_SMALL_2: 'anstelle von Ablauf:',

  T_SHOP_PF_MESSAGE_NO_PART_FOR_PF_1: 'Chargenfehler: Kein Ablauf für Teil gefunden',
  T_SHOP_PF_MESSAGE_NO_PART_FOR_PF_2: 'und',

  // delete place
  T_SHOP_PF_DIALOG_DELETE_CAPTION: 'Platz löschen',
  T_SHOP_PF_DIALOG_DELETE_CONFIRM_CAPTION: 'Bestätigung vor dem Löschen',
  T_SHOP_PF_DIALOG_DELETE_CONFIRM_TEXT: 'Wollen Sie alle Daten von Platz-Nr. (Ablauf + Inhalt) löschen?',
  T_SHOP_PF_DIALOG_DELETE_PERSON_CAPTION: 'Bitte Person auswählen, die die Charge entladen hat',
  T_SHOP_PF_DIALOG_DELETE_PERSON_ERROR: 'Ohne Angabe der entladenden Person kann der Platz nicht gelöscht werden',
  T_SHOP_PF_DIALOG_DELETE_RUNNING_PERMISSION_ERROR: 'Keine Berechtigung zum Löschen einer laufenden Charge',
  T_SHOP_PF_DIALOG_DELETE_PLACE_NOT_FOUND_ERROR: 'Fehler: Platz in DB (b_wp_aut) nicht gefunden:',

  // move place
  T_SHOP_PF_DIALOG_MOVE_TEXT: 'Daten von diesem Platz auf Platz-Nr. verschieben',

  // manual command
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_CAPTION: 'Mit Handbefehl auf Platz fahren',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_TEXT: 'Eingabe Handbefehl Ziel-Platz:',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_NOT_VALID: 'Fehler Platz {0} nicht zulässig',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_COMMAND_FOR_UNLOAD: 'Kein Handbefehl zu Entlade-Plätze erlaubt',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_COMMAND_FOR_LOADING: 'Kann keine Charge zu der Entlade-Station verschieben',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_NOT_FOUND_B_WP_AUT: 'Fehler: Platz: {0} nicht gefunden in DB b_wp_aut',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_CANNOT_BE_GIVEN: 'Eine Anlage als Zielplatz für Handbefehl ist nicht erlaubt. Bitte einen Ablauf benutzen.',
  T_SHOP_PF_DIALOG_MANUAL_COMMAND_ERROR_PLACE_IS_NOT_OCCUPIED: 'Fehler:  Platz: {0} ist nicht belegt',

  // Cancel manual command
  T_SHOP_PF_DIALOG_CANCEL_MANUAL_COMMAND: 'Wollen Sie den Handbefehl wirklich löschen ?',

  // Reactivate pf
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_TEXT: 'Reaktivierung einer (aus Versehen) geloeschten Ablauf-Nr (ohne Bindestrich: JJNNNNN (JJ=Jahr NNNNN=Nummer)',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_NOT_FOUND: 'Ablauf-Nr nicht gefunden',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_ALREADY_ON_PLACE: 'Diese Ablauf-Nr steht schon auf Platz',
  T_SHOP_PF_DIALOG_REACTIVATE_COMMAND_ERROR_PF_INPUT: 'Eine Ablauf-Nr muss genau 7 Stellen haben (2=Jahr  5=Nummer)',

  T_SHOP_PF_DIALOG_CAPTION_INFORMATION: 'Information',
  T_SHOP_PF_DIALOG_CAPTION_MOVE: 'Verschieben',
  T_SHOP_PF_DIALOG_CAPTION_CONFIRMATION_OVERWRITE: 'Bestätigung vor dem Überschreiben',
  T_SHOP_PF_DIALOG_CAPTION_CONFIRM: 'Bitte bestätigen',

  T_SHOP_PF_BTN_LOCK_CAPTION: 'Sperren',
  T_SHOP_PF_BTN_LOCK_TOOLTIP: 'Platz sperren',
  T_SHOP_PF_BTN_LOCK_UNLOCK_CAPTION: 'Sperre aufheben',
  T_SHOP_PF_BTN_LOCK_UNLOCK_TOOLTIP: 'Platz entsperren',

  T_SHOP_PF_BTN_UNLOAD_CAPTION: 'Abpacken',
  T_SHOP_PF_BTN_UNLOAD_TOOLTIP: 'Entladen',

  T_SHOP_PF_BTN_LOAD_PARTS_CAPTION: 'Teile packen',
  T_SHOP_PF_BTN_LOAD_PARTS_TOOLTIP: 'Teile packen',

  T_SHOP_PF_BTN_MODIFY_PROCESS_FLOW_CAPTION: 'Ablauf Eingriff',
  T_SHOP_PF_BTN_MODIFY_PROCESS_FLOW_TOOLTIP: 'Ablauf Eingriff',

  T_SHOP_PF_BTN_START_PROCESS_FLOW_CAPTION: 'Start freigeben',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_TOOLTIP: 'Start freigeben',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_MODIFY_TIME_CAPTION: 'Startzeit verändern',
  T_SHOP_PF_BTN_START_PROCESS_FLOW_MODIFY_TIME_TOOLTIP: 'Startzeit verändern',

  T_SHOP_PF_BTN_ENTER_PROCESS_FLOW_CAPTION: 'Ablauf Eingabe',
  T_SHOP_PF_BTN_ENTER_PROCESS_FLOW_TOOLTIP: 'Eingabe Ablauf',

  T_SHOP_PF_BTN_MANUAL_CAR_COMMAND_CAPTION: 'Handbefehl',
  T_SHOP_PF_BTN_MANUAL_CAR_COMMAND_TOOLTIP: 'Handbefehl',

  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_CAPTION: 'Handbef.Abbr.',
  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_TOOLTIP: 'Handbefehl abbrechen',
  T_SHOP_PF_BTN_CANCEL_CAR_COMMAND_CAPTION_TO: 'Handbef. Abbr. zu',

  T_SHOP_PF_BTN_DELETE_PLACE_CAPTION: 'Platz löschen',
  T_SHOP_PF_BTN_DELETE_PLACE_TOOLTIP: 'Platz löschen',
  T_SHOP_PF_BTN_DELETE_PLACE_UNLOAD_CAPTION: 'Abpacken',
  T_SHOP_PF_BTN_DELETE_PLACE_UNLOAD_TOOLTIP: 'Abpacken',

  T_SHOP_PF_BTN_OCCUPY_CAPTION: 'Belegen',
  T_SHOP_PF_BTN_OCCUPY_TOOLTIP: 'Belegen',

  T_SHOP_PF_BTN_SHOW_LOADS_CAPTION: 'Chargen anzeigen',
  T_SHOP_PF_BTN_SHOW_LOADS_TOOLTIP: 'Chargen anzeigen',
  T_SHOP_PF_BTN_REACTIVATE_PROCESS_FLOW_CAPTION: 'Ablauf reaktivieren',
  T_SHOP_PF_BTN_REACTIVATE_PROCESS_FLOW_TOOLTIP: 'Gelöschten Ablauf auf leerem Gestell reaktivieren',

  T_SHOP_PF_BTN_STEP_MANIPULATION_CAPTION: 'Schritt Manipulation',
  T_SHOP_PF_BTN_STEP_MANIPULATION_TOOLTIP: 'Schritt überspringen',

  T_SHOP_PF_BTN_MOVE_TO_PLACE_CAPTION: 'Verschieben',
  T_SHOP_PF_BTN_MOVE_TO_PLACE_TOOLTIP: 'Verschieben',

  T_SHOP_PF_BTN_SAVE_IMAGE: 'Bild speichern',
  T_SHOP_PF_BTN_SAVE_IMAGE_EXISTS: 'Bild vorhanden',

  T_SHOP_PF_BTN_LOAD_TESTED_CAPTION: 'Prüfcharge abschliessen/bearbeiten',
  T_SHOP_PF_BTN_LOAD_TESTED_TOOLTIP: 'Prüfcharge abschliessen/bearbeiten',
  T_SHOP_PF_BTN_LOAD_TESTED_TEST_LOAD_CAPTION: 'Prüfcharge starten',
  T_SHOP_PF_BTN_LOAD_TESTED_TEST_LOAD_TOOLTIP: 'Prüfcharge überprüfen',
  T_SHOP_PF_BTN_LOAD_TESTED_CHECK_OK_CAPTION: 'Prüfung OK',
  T_SHOP_PF_BTN_LOAD_TESTED_CHECK_OK_TOOLTIP: 'Zwischenprüfung wurde durchgeführt',
  T_SHOP_PF_BTN_LOAD_TESTED_PROCESS_CHECK_OK_CAPTION: 'Prozesskontrolle OK',
  T_SHOP_PF_BTN_LOAD_TESTED_PROCESS_CHECK_OK_TOOLTIP: 'Prozesskontrolle wurde durchgeführt',
  T_SHOP_PF_BTN_LOAD_TESTED_WASH_CHECK_OK_CAPTION: 'Waschkontrolle OK',
  T_SHOP_PF_BTN_LOAD_TESTED_WASH_CHECK_OK_TOOLTIP: 'Waschkontrolle wurde durchgeführt',
  T_SHOP_PF_BTN_LOAD_TESTED_END_WAIT_CAPTION: 'Warten beenden',
  T_SHOP_PF_BTN_LOAD_TESTED_END_WAIT_TOOLTIP: 'Warten ist beendet',

  T_SHOP_PF_BTN_CHARGE_IN_QS_WORK_CAPTION: 'in QS-Bearb. starten/stoppen',
  T_SHOP_PF_BTN_CHARGE_IN_QS_WORK_TOOLTIP: 'in QS-Bearbeitung starten/stoppen',

  T_SHOP_PF_CHECKBOX_TEST_LOAD_CAPTION: 'Prüfcharge',
  T_SHOP_PF_CHECKBOX_TEST_LOAD_TOOLTIP: 'Prüfcharge',
  T_SHOP_PF_CHECKBOX_END_TEST_LOAD_CAPTION: 'End-Prüfung Eilig',
  T_SHOP_PF_CHECKBOX_END_TEST_LOAD_TOOLTIP: 'End-Prüfung Eilig',
  T_SHOP_PF_CHECKBOX_NIO_TEST_LOAD_CAPTION: 'Prüfung NIO',
  T_SHOP_PF_CHECKBOX_NIO_TEST_LOAD_TOOLTIP: 'Prüfung NIO',
  T_SHOP_PF_CHECKBOX_IN_QA_WORK_CAPTION: '..in QS-Bearbeitung..',
  T_SHOP_PF_CHECKBOX_IN_QA_WORK_TOOLTIP: '..in QS-Bearbeitung..',

  T_SHOP_PF_UNLOAD_SELECT_PLACE_CAPTION: 'Bitte Entlade-Platz auswählen:',
  T_SHOP_PF_UNLOAD_ERROR_MAGAZINE_ONLY: 'Abpacken ist nur auf Magazin-Plätzen möglich',
  T_SHOP_PF_UNLOAD_ERROR_INVALID_OPTION: 'Ungültiger Platz für das Entladen angegeben!',
  T_SHOP_PF_UNLOAD_ERROR_ALREADY_TARGET: ' (Platz schon als Ziel von Platz: {0})',
  T_SHOP_PF_UNLOAD_ERROR_CAR_APPROACHED: ' (Platz wird durch einen Fahrwagen im Moment angefahren)',

  T_SHOP_PF_TEST_DIALOG_TITLE: 'Charge geprüft',
  T_SHOP_PF_TEST_DIALOG_TEXT: 'Eingabe Name und Datum',
  T_SHOP_PF_TEST_DIALOG_INPUT_NAME_DATE: 'Name und Zeit eingeben',
  T_SHOP_PF_TEST_DIALOG_INPUT_NAME_DATE_CAPTION: 'Charge geprüft',
  T_SHOP_PF_TEST_DIALOG_INPUT_TEST_STARTED_TEXT: 'Prüfcharge gestartet  {0} ',
  T_SHOP_PF_TEST_DIALOG_ERROR_ACTION_NOT_POSSIBLE: 'Aktion nicht möglich',
  T_SHOP_PF_TEST_DIALOG_ERROR_PLACE_NOT_FOUND: 'Fehler: Ort in DB (b_wp_aut) nicht gefunden: {0}',

  T_SHOP_PF_TEST_DIALOG_CONFIRM_NOT_READY_CHECKED: 'Charge ist noch nicht fertig! Trotzdem als geprüft kennzeichnen?',
  T_SHOP_PF_TEST_ENTER_RESULT_DIALOG_TEXT: 'Wollen Sie ein Prüfergebnis eingeben?',
  T_SHOP_PF_TEST_DIALOG_RESULT_SELECTION_PASSED: 'Test bestanden',
  T_SHOP_PF_TEST_DIALOG_RESULT_SELECTION_NOT_PASSED: 'Test nicht bestanden',

  // LOAD INFO
  T_LOAD_STATUS_WAIT_FOR: 'Warte auf',
  T_LOAD_STATUS_PLANNED_START: 'gepl. Start',
  T_LOAD_STATUS_EXTERNAL_START: 'externer Start',
  T_LOAD_STATUS_NOT_READY: 'NICHT BEREIT!',
  T_LOAD_STATUS_CONT_MANUAL: 'Regler LOKAL',
  T_LOAD_STATUS_STEP_1: 'von',
  T_LOAD_STATUS_STEP_2: 'Schritt-Nr.',
  T_LOAD_STATUS_RECIPE_FINISHED: 'Beh. Fertig',
  T_LOAD_STATUS_OCCUPIED: 'Belegt',
  T_LOAD_STATUS_WILL_START: 'will starten',
  T_LOAD_STATUS_WAIT_FOR_RECIPE: 'wartet auf Beh.',
  T_LOAD_STATUS_WAIT_FOR_LOAD: 'wartet auf Charge',
  T_LOAD_STATUS_GET_OUT: 'will raus',
  T_LOAD_STATUS_RECIPE: 'Beh.',
  T_LOAD_STATUS_WANT: 'will',
  T_LOAD_STATUS_PF_COMPLETED: 'Ablauf Fertig',
  T_LOAD_STATUS_LOCKED: 'gesperrt',
  T_LOAD_STATUS_PRIORITY: 'Prio',
  T_LOAD_STATUS_NOT_OCCUPIED: '! nicht belegt !',
  T_LOAD_STATUS_NO_TAG: 'no Tag',
  T_LOAD_STATUS_PREPARE: 'VORBEREITEN',
  T_LOAD_STATUS_RESERVED_BY: 'Reserviert von',

  // ALLGEMEINE FEHLER
  T_GENERAL_ERROR_NOT_IMPLEMENTED: 'Nicht implementiert',
  T_GENERAL_ERROR_UNSUPPORTED_ON_WORKPLACE: 'Nicht unterstützt am Arbeitsplatz',
  T_GENERAL_ERROR_CAPTION: 'Fehler',
  T_GENERAL_WARNING_CAPTION: 'Warnung',

  // Globap name of part
  T_GENERAL_PART_NAME_1: 'Teile-Nr.',
  T_GENERAL_PART_NAME_2: 'Käfig-Nr.',
  T_GENERAL_PART_NAME_3: 'Bosch-Ch.-Nr.',
  T_GENERAL_PART_NAME_4: 'Ident-Nr',

  // CHARGENDIALOG-FEHLER
  T_LOAD_DIALOG_ERROR_NO_PARTS: 'Keine Teile angegeben!',
  T_LOAD_DIALOG_ERROR_ORDER: 'Auftrag (Pos) \'{0}\' wurde gefunden!',
  T_LOAD_DIALOG_ERROR_PLACE_STATE: 'Der platz muss belegt und ohne Daten sein (== leerer Platz).',
  T_LOAD_DIALOG_ERROR_PLACE_ALREADY_OCCUPIED: 'Der Platz ist bereits mit Daten belegt.',
  T_LOAD_DIALOG_ERROR_NOT_OCCUPIED_OR_EMPTY: 'Der Platz ist nicht belegt oder das Tray ist leer!',
  T_LOAD_DIALOG_ERROR_PLACE_LOCKED: 'Dieser Platz ist gesperrt!',
  T_LOAD_DIALOG_ERROR_PF_FROM_ORDER_NOT_FOUND: 'Prozessablauf aus dem Auftrag mit pfTemplateId={0} wurde nicht gefunden.',
  T_LOAD_DIALOG_ERROR_NO_BOX_NO_FREE: 'Keine Box-Nr. frei --> Bitte drücken Sie auf Command-Repair...',
  T_LOAD_DIALOG_ERROR_CREATING_LOAD: 'Fehler beim Erstellen einer neuen Ablauf-Charge',
  T_LOAD_DIALOG_ERROR_NO_LOAD_FOUND: 'Keine Beladung am Arbeitsplatz {0} gefunden',
  T_LOAD_DIALOG_ERROR_NO_PART_INFO_FOUND: 'Keine Informationen zu geladenen Teilen für den Arbeitsplatz gefunden!',
  T_LOAD_DIALOG_ERROR_LOAD_ALREADY_ENABLED: 'Die Beladung ist bereits für das automatische Laden aktiviert!',
  T_LOAD_DIALOG_ERROR_NO_A_LD_TMT_HEAD: 'Fehler: a_ld_tmt_head nicht gefunden für loadId={0}, Platz von {1}',
  T_LOAD_DIALOG_ERROR_NO_LOAD: 'Fehler: Beladung am Platz {0} nicht gefunden, a_ld.id={1}',

  T_GANNT_PLANNING_NO_PLANNING_INFO: 'Keine Planungsinformation verfügbar',
  T_GANNT_PLANNING_NO_CAR_COMMAND: 'Kein Wagenbefehl verfügbar',

  // BELT FURNACES
  // Loading dialog
  T_BELT_LOAD_START_ERROR_LOAD_NOT_FOUND: 'Chargendaten konnten nicht bestimmt werden.',
  T_BELT_LOAD_START_ERROR_LOAD_NOT_FOR_BELT: 'Nur für Bandofenchargen erlaubt!',
  T_BELT_LOAD_START_ERROR_ORDER_ALREADY_ACTIVE: 'Nicht möglich, Auftrag ist schon aktiv.',
  T_BELT_LOAD_START_ERROR_ONLY_ONE_ACTIVE_ORDER: 'Es kann nur einen AKTIVEN Auftrag geben',
  T_BELT_LOAD_START_ERROR_LOAD_ALREADY_STARTED: 'Nicht möglich, Charge schon gestarted.',
  T_BELT_LOAD_START_ERROR_LOAD_WAITING_FOR_CONDITION: 'Nicht möglich, da schon eine gestartete Charge nach dieser Charge existiert',
  T_BELT_LOAD_START_ERROR_ACTION_NOT_ALLOWED_FOR_ORDERS: 'Aktion für Aufträge nicht erlaubt',
  T_BELT_LOAD_START_NEW_STATUS_WAS_FORCED: 'Neuer Zustand wurde erzwungen.',
  T_BELT_LOAD_START_EXECUTION_STOPPED: 'Ausführung abgebrochen.',
  T_BELT_LOAD_START_ACTIVATE_ORDER_CONFIRMATION: 'Wollen Sie diesen Auftrag aktiv machen?',
  T_BELT_LOAD_START_LOAD_CONFIRMATION: 'Wollen Sie wirklich die Charge starten?',
  T_BELT_LOAD_START_LOAD_CONDITION_NOT_REACHED_CONFIRMATION: 'Bedingungen wurden noch nicht erreicht. Sind Sie sicher, dass Sie den Chargen-Status erzwingen wollen?',
  T_BELT_LOAD_START_LOADING_CONFIRMATION: 'Wollen Sie wirklich die Beladung starten?',
  T_BELT_LOAD_START_LOADING_ERROR_NOT_FOR_THIS_CHARGE: 'Nicht möglich mit dieser Charge.',
  T_BELT_LOAD_FINISH_LOADING_DELETE_ORDER_CONFIRMATION: 'Wollen Sie WIRKLICH den Auftrag (mit allen noch nicht gestarteten Chargen) löschen?',
  T_BELT_LOAD_FINISH_LOADING_STOP_CONFIRMATION: 'Wollen Sie wirklich die Beladung stoppen?',

  // BATCH FURNACES
  T_BATCH_ERROR_CHAMBER_NOT_REMOTE_OR_ACTIVE: 'Kammer nicht remote oder nicht bereit ...',

  // SONSTIGES
  T_END: 'Ende',
} satisfies Record<Tpt9800TrlTokens, string>;

const batchRecipeItemVersion: Partial<TComponentPT9800Keys<'batchRecipeItemVersion'>> = {
  correctionProfile: 'Korrekturprofil',
  positions: 'Schritte',
};

const digitalInput: Partial<TComponentPT9800Keys<'digitalInput'>> = {
  useForEndControl: 'Als Endbedingung verwenden',
};

export default {
  batchRecipeItemVersion,
  beltRecipeItemHeader,
  digitalInput,
  furnaceAlarm,
  load,
  loadState,
  material,
  planLoadInput,
  plannedPartInput,
  processFlow,
  processFlowContent,
  processFlowListItem,
  processFlowManualCommand,
  pt9800Translations,
  pusherRecipeItemHeader,
  pusherRecipeRuntimeInfo,
  recipeRuntimeInfo,
  recipeItemHeader,
  recipeItemPosition,
  recipeItemPositionHeader,
  recipeListItem,
  setPointItem,
  WorkPlaceStatus,
  WorkPlaceStatusStatus,
};
